import { Check, ShieldCheck } from 'lucide-react'

import {
  BarList,
  Button,
  DataTable,
  EmptyState,
  LoadingSkeleton,
  PageHeader,
  QueryBoundary,
  Section,
  StatTile,
  StatusBadge,
  type Column,
} from '../../../components'
import { useIsPlatformOwner } from '../../../app/session'
import { useHealth, type HealthItem } from '../../../fixtures'
import { formatGb, formatPercent, shareOf } from '../../../lib/format'
import { t } from '../../../lib/i18n'
import { useNotifyOwner } from '../notifyOwner'
import { useSuggestUpgradeBroadcast } from '../suggestUpgrade'

/**
 * P-08 System health.
 *
 * Operational, not financial — there is no money on this screen at all. Every
 * row in "Needs a look" ends in something the reader can DO, or in a settled
 * status, never in a number to admire.
 *
 * ---------------------------------------------------------------------------
 * THE ROW ACTIONS, AND WHO EACH ONE IS ADDRESSED TO
 * ---------------------------------------------------------------------------
 * **"Suggest {plan}"** is the prefilled broadcast §6.1 P-07 asks for. It shares
 * P-07's composer (`suggestUpgrade.tsx`) because it is the same act:
 * `audience: { nearLimit: true }`, seeded text, a human who edits and confirms.
 * The row supplies the words, not the address — the composer says so.
 *
 * **"Notify owner"** is addressed to exactly this book. It was removed for a
 * while, and correctly: `broadcastRequest.audience` filters on `status`,
 * `planCode` and `nearLimit` with no way to name a tenant, so the only thing
 * behind the button would have been a filter that happened to match one man —
 * a message to an audience nobody chose. `POST /platform/businesses/:id/notify`
 * (P14) now names the tenant in its path, so the control is back, on
 * `notifyOwner.tsx`. It renders for BOTH platform roles, unlike the broadcast:
 * telling one trader his upload failed is what a support account is for, and
 * nothing here would be refused, so nothing here is hidden.
 *
 * An INVESTIGATE row gets no message at all. `health.service.ts` is explicit
 * about the difference — a failed transcode is the trader's problem and he is
 * told; a failed backup is ours and somebody looks into it.
 */

/** The only plan this screen ever names. Codes are stored; labels are looked up. */
const traderPlan = () => t('p.plan.TRADER')

function WhatCell({ item }: { item: HealthItem }) {
  // A row that is merely informational ("approaching the limit") is not a
  // warning; only the ones that actually went wrong are toned.
  const wentWrong = item.action !== 'SUGGEST_TRADER'

  return (
    <span>
      <b className={wentWrong ? 'font-display font-bold text-warn' : 'font-semibold text-dark'}>
        {item.what}
      </b>
      {item.detail ? (
        <span className="text-muted">
          <span aria-hidden="true"> · </span>
          {item.detail}
        </span>
      ) : null}
    </span>
  )
}

export function HealthScreen() {
  const health = useHealth()
  const isOwner = useIsPlatformOwner()

  /* Shared with P-07. This screen does not know how many books are near a
     limit — that figure belongs to `GET /platform/usage` — so no count is
     passed and the copy names none rather than guessing one. */
  const suggest = useSuggestUpgradeBroadcast()

  /* P14. Unlike the broadcast this one has an address, so it is not gated on
     the role — `role: 'both'` in the registry, and a support account pressing
     it is not refused. */
  const notify = useNotifyOwner()

  const columns: Column<HealthItem>[] = [
    {
      id: 'what',
      header: t('label.what'),
      cell: (item) => <WhatCell item={item} />,
    },
    {
      id: 'business',
      header: t('label.business'),
      sortBy: (item) => item.business,
      cell: (item) => item.business,
    },
    {
      id: 'when',
      header: t('label.when'),
      cell: (item) => <span className="whitespace-nowrap text-sub">{item.when}</span>,
    },
    {
      id: 'action',
      header: t('p.health.colAction'),
      numeric: true,
      cell: (item) => {
        if (item.action === 'SUCCEEDED') {
          return <StatusBadge tone="ok">{t('p.health.succeeded')}</StatusBadge>
        }

        if (item.action === 'SUGGEST_TRADER') {
          /* Broadcast is `platform.settings` — PLATFORM_OWNER only, enforced by
             the API. Support gets the observation instead of a control that
             could only ever be refused; this console gates by ABSENCE. */
          return isOwner ? (
            <Button
              size="sm"
              onClick={() => suggest.open({ name: item.business, plan: traderPlan() })}
            >
              {t('p.health.suggestPlan', { plan: traderPlan() })}
            </Button>
          ) : (
            <StatusBadge tone="accent">{t('p.health.wouldSuit', { plan: traderPlan() })}</StatusBadge>
          )
        }

        if (item.action === 'INVESTIGATE') {
          // Nobody is written to for this one — it is the platform's own
          // failure, and a message would be telling a trader about our backups.
          return <StatusBadge tone="neutral">{t('p.health.investigate')}</StatusBadge>
        }

        /* NOTIFY_OWNER. `tenantId` is nullish in the contract and the route
           addresses a tenant in its path, so a row that names no book has
           nobody to write to — and says that instead of offering a button that
           could only ever be pressed at nothing. */
        const { businessId } = item
        return businessId ? (
          <Button
            size="sm"
            onClick={() => notify.open({ businessId, business: item.business, what: item.what })}
          >
            {t('act.notifyOwner')}
          </Button>
        ) : (
          <StatusBadge tone="neutral">{t('p.health.noBusinessOnRow')}</StatusBadge>
        )
      },
    },
  ]

  return (
    <>
      {/* The "All good" indicator is a claim about the platform, so it waits
          for the answer rather than asserting it while the page is loading. */}
      <PageHeader
        title={t('p.health.title')}
        subtitle={
          health.data ? t('p.health.subtitle', { count: health.data.backupsTotal }) : undefined
        }
        actions={
          health.data ? (
            <StatusBadge tone="ok" icon={<Check className="size-3" />}>
              {t('p.health.allGood')}
            </StatusBadge>
          ) : undefined
        }
      />

      <QueryBoundary
        query={health}
        skeleton={
          <>
            <LoadingSkeleton variant="tiles" />
            <LoadingSkeleton variant="table" rows={4} className="mt-3.5" />
          </>
        }
      >
        {(data) => (
          <>
            <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
              <StatTile
                label={t('p.health.backedUpLastNight')}
                value={
                  <span className="text-success">
                    {t('p.health.backupRatio', {
                      done: data.backupsDone,
                      total: data.backupsTotal,
                    })}
                  </span>
                }
                sub={
                  data.retries === 0
                    ? t('p.health.backedUpSubNone')
                    : t('p.health.backedUpSub', { count: data.retries })
                }
              />
              <StatTile
                label={t('p.health.videosQueued')}
                value={data.videoQueue}
                sub={
                  /* `longestWaitMinutes` is in the payload. An empty queue has
                     no longest wait, so it says that rather than "0 min". */
                  data.videoQueue === 0
                    ? t('p.health.videosQueuedNone')
                    : t('p.health.videosQueuedSub', { count: data.longestWaitMinutes })
                }
              />
              <StatTile
                label={t('p.health.failed24h')}
                value={<span className="text-warn">{data.failed24h}</span>}
                sub={t('p.health.failed24hSub')}
              />
              <StatTile
                label={t('p.health.storageUsed')}
                value={formatGb(data.storageUsedGb)}
                sub={t('p.health.storageUsedSub', {
                  // A `null` limit is unlimited, and there is no percentage of
                  // unlimited. Both helpers say so rather than printing 0.
                  percent: formatPercent(shareOf(data.storageUsedGb, data.storageLimitGb)),
                  total: formatGb(data.storageLimitGb),
                })}
              />
            </div>

            <div className="mt-3.5 grid gap-3.5 lg:grid-cols-[1.55fr_1fr]">
              <Section title={t('p.health.needsALook')}>
                <DataTable
                  columns={columns}
                  rows={data.items}
                  rowKey={(item) => item.id}
                  caption={t('p.health.needsALook')}
                  empty={
                    <EmptyState
                      icon={<ShieldCheck className="size-6" />}
                      title={t('p.health.emptyTitle')}
                      body={t('p.health.emptyBody')}
                    />
                  }
                />
              </Section>

              <Section title={t('p.health.biggestStores')}>
                <BarList
                  items={data.biggestStores.map((store) => ({
                    id: store.name,
                    label: store.name,
                    value: store.gb,
                    display: formatGb(store.gb),
                  }))}
                />
                <p className="mt-3.5 border-t border-muted/15 pt-3.5 text-xs leading-relaxed text-muted">
                  {t('p.health.storesNote')}
                </p>
              </Section>
            </div>

            {suggest.element}
            {notify.element}
          </>
        )}
      </QueryBoundary>
    </>
  )
}
